import { AlertTriangle, History } from 'lucide-react';

import type { CardProblemKind } from '@/lib/flow-utils';

/** The sentence each paused kind says, in one place so the two card types cannot drift apart. */
export const PAUSE_MESSAGE: Record<CardProblemKind, string> = {
  incomplete: 'task.flow.rule.pausedIncomplete',
  broken: 'task.flow.rule.pausedBroken',
  drifted: 'task.flow.rule.pausedDrifted',
  unreachable: 'task.flow.rule.pausedUnreachable',
  targetDeleted: 'task.flow.rule.pausedTargetDeleted',
  noEffects: 'task.flow.rule.pausedNoEffects',
};

/**
 * Why a card will not run, in the ONE place a card says so.
 *
 * There were two. An event card carried a `problem` line under its header AND a paused line at
 * the foot, which read as two different findings while both were the same one — and once the
 * vocabulary was unified they said nearly the same words twice. A Change card had neither, then
 * briefly had the top one only, so the two card types disagreed about where to look.
 *
 * The foot is the right home: it is beside the controls that RESOLVE the problem (Accept, and the
 * detail of what drifted), and it is the one strip of a collapsed card that stays reserved for
 * exactly this. A notice above the body would push the card's own content down every time
 * something needed attention, which is when the content matters most.
 *
 * A duplicate is NOT a pause -- the card still runs -- but it is still WRONG, and it was muted
 * grey on the first pass, which read as a footnote rather than something to act on. Two identical
 * changes are never what a teacher meant: the second does nothing the first does not, so one of
 * them is a mistake. It gets the same amber as a pause, because the question it answers is the
 * same one ("does this need me?"), and the sentence is what distinguishes them.
 *
 * It still yields to a pause: a card that cannot run at all has a more urgent thing to say than
 * that another card says the same thing.
 */
export function FlowPausedNotice({ pausedMessage, duplicateMessage, muted }: {
  pausedMessage?: string;
  duplicateMessage?: string;
  /**
   * A fact the teacher has ACCEPTED, so it keeps its words and loses its alarm.
   *
   * Amber asks for attention. Once a starved All Correct has been signed off there is nothing
   * left to attend to and the sentence is only there so the card still says why it is inert —
   * leaving it amber is how a panel teaches people to stop reading its warnings.
   */
  muted?: boolean;
}) {
  if (pausedMessage) {
    return (
      <p className={`flex items-center gap-1.5 text-xs ${muted
        ? 'italic text-ws-muted' : 'font-medium text-ws-warning-fg'}`}>
        <History aria-hidden='true' className='h-3.5 w-3.5 shrink-0' />
        {pausedMessage}
      </p>
    );
  }
  if (duplicateMessage) {
    return (
      <p className='flex items-center gap-1.5 text-xs font-medium text-ws-warning-fg'>
        <AlertTriangle aria-hidden='true' className='h-3.5 w-3.5 shrink-0' />
        {duplicateMessage}
      </p>
    );
  }
  return null;
}
